<template lang="pug">
.k-meeting-file-input
  .k-meeting-file-input__image-box.pa-4(v-if="image || filename")
    .d-flex.justify-space-between.align-top
      k-button(@click="clear" small text).ma-0
        k-icon(size="18") clear
      k-button(text @click="$emit('send-media')" :disabled="isSendingMedia" small).ma-0
        k-icon(size="18") send
    k-loader(:value="isSendingMedia" small)
      v-img(v-if="image" :src="image" :max-height="250" contain)
      template(v-else-if="filename")
        .paragraph--small {{ filename }}
  input(
    type="file"
    @change="onFileChange"
    ref="fileUpload"
  ).k-meeting-file-input__button
  k-field-textarea(
    ref="input"
    v-model="message.text"
    small
    auto-grow
    row-height="36"
    @focus="$emit('focus')"
    hide-details
    inputmode="search"
    :placeholder="`Message ${name}`"
    :readonly="!isConnected"
    :loading="!isConnected"
    @keypress.native.exact.enter.prevent="$emit('send-message')"
  )
    template(#append)
      v-fade-transition
        k-icon(
          v-show="!!message.text.trim()"
          size="18"
          color="brand-pink"
          class="hidden-md-and-up"
          @click="$emit('send-message')"
        ) send
    template(v-slot:prepend-inner)
      k-button(@click="triggerUploadClick" text small).mx-0.k-meeting-file-input__attach
        k-icon(color="brand-warm-grey" size="15") paperclip
</template>

<script>
export default {
  props: {
    name: {
      type: String,
      default: ''
    },
    isConnected: {
      type: Boolean,
      default: false
    },
    isSendingMedia: {
      type: Boolean,
      required: true
    }
  },
  data () {
    return {
      image: null,
      filename: null,
      message: {
        text: '',
        file: null,
        imageData: null
      }
    }
  },
  watch: {
    // emit changes to the message object
    message: {
      handler (e) {
        this.$emit('input', e)
      },
      immediate: true
    }
  },
  mounted () {
    // event listener for pasting
    window.addEventListener('paste', e => {
      // TODO handle pasting of other file types

      // Handle pasted images
      this.retrieveImageFromClipboardAsBlob(e, imageBlob => {
        // If there's an image, display it in the canvas
        if (imageBlob) {
          this.handleFileUpload(imageBlob)
        }
      })
    }, false)
  },
  methods: {
    triggerUploadClick () {
      this.$refs.fileUpload.click()
    },
    /**
     * This handler retrieves the images from the clipboard as a blob and returns it in a callback.
     *
     * @param e
     * @param callback
     */
    retrieveImageFromClipboardAsBlob (e, callback) {
      if (e.clipboardData === false) {
        if (typeof (callback) === 'function') {
          callback(undefined)
        }
      }

      const items = e.clipboardData.items

      if (items === undefined) {
        if (typeof (callback) === 'function') {
          callback(undefined)
        }
      };

      for (let i = 0; i < items.length; i++) {
        // Skip content if not image
        if (!items[i].type.includes('image')) { continue }
        // Retrieve image on clipboard as blob
        const blob = items[i].getAsFile()
        if (typeof (callback) === 'function') {
          callback(blob)
        }
      }
    },
    /**
     * Handles creating a FormData object, emitting the message
     * and displaying the image if the file is an image
     * @param file
     */
    handleFileUpload (file) {
      const formData = new FormData()
      formData.append('file', file)

      this.message.file = formData

      if (file.type.includes('image')) {
        this.createImage(file)
      } else {
        // emit formData
        this.$emit('input', this.message)
      }

      // set the filename
      this.filename = file.name
    },
    /**
     * Handles changes when user uploads a file using the button
     * Gets the required file information
     * Calls fileUpload method
     */
    onFileChange (e) {
      const files = e.target.files || e.dataTransfer.files
      if (!files.length) {
        return
      }
      const file = e.target.files[0]
      this.handleFileUpload(file)

      // blur the text field
      this.$refs.input.$refs.field.blur()
    },
    /**
     * Set the image url to the data image, so it can be displayed
     */
    createImage (image) {
      const reader = new FileReader()
      const pic = new Image()
      const vm = this

      // on loading picture to image
      // set the image data height and width
      pic.onload = () => {
        vm.message.imageData = {
          height: pic.height,
          width: pic.width
        }
        // emit this data
        vm.$emit('input', vm.message)
      }

      // on loading image file
      // set the src url
      // set the src for the image to get data
      reader.onload = e => {
        vm.image = e.target.result
        pic.src = this.image
      }

      // load image to reader
      reader.readAsDataURL(image)
    },
    /**
     * Clear uploaded file info
     */
    clear () {
      this.message.file = null
      this.image = null
      this.filename = null

      // clear the file input value
      this.$refs.fileUpload.value = ''
    }
  }
}
</script>

<style scoped lang="scss">
.k-meeting-file-input {
  &__image-box {
    background-color: white;
    border: 2px solid var(--v-brand-grey-base) !important;
    border-radius: 4px;
  }

  &__button {
    display: none;
  }

  // styling the attach button
  &__attach {
    height: 28px !important;

    &:hover {
      background-color: var(--v-brand-pink-base) !important;
      opacity: .8 !important;

      svg {
        color: white !important;
      }
    }
  }

  ::v-deep {
    .k-button {
      min-width: 28px !important;
    }

    .v-input__prepend-inner {
      padding-left: 6px !important;
    }

    // remove button padding
    .v-btn__content {
      padding: 0;
    }

    .v-input__slot {
      background: white !important;
    }

    textarea {
      // 10 rows
      max-height: 180px;
      overflow: auto;
      align-self: center;
    }

    .v-input__append-inner {
      padding-right: 12px;

      @include md-and-up {
        display: none;
      }
    }
  }
}
</style>
